/*
 * Copyright (C) 2019-2022 Corvalent Corporation - All Rights Reserved
 * This software is licensed program property and is Proprietary and Confidential.
 * Unauthorized copying of this file via any medium is strictly prohibited unless
 * prior written permission has been obtained by Corvalent Corporation.
 * In addition, the look and feel of this software as well as the user interface
 * design is also Copyright ©.
 * Notice: This software is not open source.
 * This software is subject to the Corvalent Software License Agreement.
 * You should have received a copy of this license with the software.
 * If not, please write to: sales@corvalent.com
 * All information contained herein is, and remains, the property of
 * Corvalent Corporation and its suppliers, if any. This software may also
 * be covered by U.S. and Foreign Patents, patents in progress, and are
 * protected by trade secret or copyright law
 */

import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import axios from 'axios';
import { setReplacer, setReviver } from './cacheUtils';

const instance = axios.create({
  baseURL: `${process.env.REACT_APP_URI}`,
});

const TOKEN_KEY = '@corvalent-Token';
const REFRESH_TOKEN_KEY = '@corvalent-Refresh-Token';
const USER_KEY = 'user';
const LAST_SELECTED_LOCATION_KEY = 'lastSelectedLocation';
const FILTER_KEY = 'filter';
const ZOOM_KEY = 'zoom';

// ---------------- AUTH ----------------

export const logout = async resetUser => {
  try {
    await signOut();
  } catch (err) {
    console.error(err);
  } finally {
    resetUser?.();
  }
};

export const userInfo = userToken =>
  instance.get('/api/v/1/user/info', {
    headers: {
      'ClearBlade-UserToken': userToken,
    },
  });

export const currentSession = async () => {
  try {
    const info = await fetchAuthSession();
    localStorage.setItem('cognitoIdentityId', info.identityId);
    const { accessToken, idToken } = info.tokens ?? {};
    return accessToken ? { accessToken, idToken } : null;
  } catch (err) {
    console.error(err);
    await signOut();
  }
  return null;
};

// ---------------- ZOOM CACHE ----------------

export const getZoomCache = () => JSON.parse(localStorage.getItem(ZOOM_KEY));

export const setZoomCache = zoom => localStorage.setItem(ZOOM_KEY, zoom);

export const removeZoomCache = () => localStorage.removeItem(ZOOM_KEY);

// ---------------- FILTER CACHE ----------------

export const getFilterCache = () =>
  JSON.parse(localStorage.getItem(FILTER_KEY), setReviver);

export const setFilterCache = filter =>
  localStorage.setItem(FILTER_KEY, JSON.stringify(filter, setReplacer));

// ---------------- LAST SELECTED LOCATION CACHE ----------------

export const getLastSelectedLocationCache = () =>
  JSON.parse(localStorage.getItem(LAST_SELECTED_LOCATION_KEY));

export const setLastSelectedLocationCache = location =>
  localStorage.setItem(LAST_SELECTED_LOCATION_KEY, JSON.stringify(location));

export const removeLastSelectedLocationCache = () =>
  localStorage.removeItem(LAST_SELECTED_LOCATION_KEY);

// ---------------- USER CACHE ----------------

export const getUserCache = () => JSON.parse(localStorage.getItem(USER_KEY));

export const setUserCache = user =>
  localStorage.setItem(USER_KEY, JSON.stringify(user));

export const removeUserCache = () => localStorage.removeItem(USER_KEY);

// ---------------- TOKEN CACHE ----------------

export const getTokenValue = () => localStorage.getItem(TOKEN_KEY);

export const setTokenValue = token => localStorage.setItem(TOKEN_KEY, token);

export const removeTokenValue = () => localStorage.removeItem(TOKEN_KEY);

// ---------------- REFRESH TOKEN CACHE ----------------

export const getRefreshTokenValue = () =>
  localStorage.getItem(REFRESH_TOKEN_KEY);

export const setRefreshTokenValue = token =>
  localStorage.setItem(REFRESH_TOKEN_KEY, token);

export const removeRefreshTokenValue = () =>
  localStorage.removeItem(REFRESH_TOKEN_KEY);
